import axios from 'axios';
import { api } from '@/lib/http';
import type {
    AiInterviewResult,
    CvScreeningResult,
    ReportKind,
    UniversalAggregateResponse,
} from '../types';

/**
 * Reports client. Backend routes mounted under /api/v2/reports (Plan 01);
 * @/lib/http already prefixes /api so callers use /v2/reports/...
 *
 * The fetch endpoints 404 when no report exists for the application; we
 * convert that to `null` so the page can render an empty state.
 */
export const reportsApi = {
    async getCvScreening(applicationId: string): Promise<CvScreeningResult | null> {
        try {
            const { data } = await api.get<{ result: CvScreeningResult | null }>(
                `/v2/reports/cv-screening/by-application/${applicationId}`,
            );
            return data.result ?? null;
        } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status === 404) return null;
            throw err;
        }
    },

    async getAIInterview(applicationId: string): Promise<AiInterviewResult | null> {
        try {
            const { data } = await api.get<{ result: AiInterviewResult | null }>(
                `/v2/reports/ai-interview/by-application/${applicationId}`,
            );
            return data.result ?? null;
        } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status === 404) return null;
            throw err;
        }
    },

    /**
     * Universal aggregate — joins JobPosting.evaluation + cv result + AI snapshot
     * into one framework-aware view. The application's TRUE score lives here.
     */
    async getUniversalAggregate(applicationId: string): Promise<UniversalAggregateResponse | null> {
        try {
            const { data } = await api.get<UniversalAggregateResponse>(
                `/v2/reports/universal/by-application/${applicationId}`,
            );
            return data;
        } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status === 404) return null;
            throw err;
        }
    },

    async editScore(params: {
        kind: ReportKind;
        resultId: string;
        componentId: string;
        editedScore: number;
        justification: string;
    }): Promise<CvScreeningResult | AiInterviewResult> {
        const path =
            params.kind === 'cv_screening'
                ? `/v2/reports/cv-screening/${params.resultId}/score`
                : `/v2/reports/ai-interview/${params.resultId}/score`;
        const { data } = await api.patch<{ result: CvScreeningResult | AiInterviewResult }>(path, {
            componentId: params.componentId,
            editedScore: params.editedScore,
            justification: params.justification,
        });
        return data.result;
    },
};
